import { Button, Icon } from '@ps/sports-ui-component-library';
import { CloseMedium } from '@ps/sports-ui-component-library/icons/CloseMedium';
import { FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { StarsButton, Translation, useResolver } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Expire from '@swf/components/NPS/Expire';
import useFeature from '@swf/core/hooks/useFeature';
import { NpsDismissalReason } from '@swf/core/models/nps';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

import {
  TXTCLI_Remove,
  TXTCLI_Yes,
  TXTMOB_Your_feedback_is_important_to__ELL,
  TXTWCL_On_a_scale_of_0___10X_how_like_ELL_1,
  TXTWCL_Thank_You,
  TXTWCL_Thank_youX_do_you_have_any_oth_ELL,
  TXTWCL_ThatXs_great_to_hearX_can_you__ELL,
  TXTWCL_WeXre_sorry_to_hear_thatX_plea_ELL
} from './NPS.messages.json';
import styles from './NPS.scss';

const options = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const getSubmissionMessage = (
  valueSubmitted: number | undefined,
  detailedResponse: boolean
): TranslationTag => {
  if (!detailedResponse || valueSubmitted === undefined)
    return TXTMOB_Your_feedback_is_important_to__ELL;
  if (valueSubmitted <= 6) return TXTWCL_WeXre_sorry_to_hear_thatX_plea_ELL;
  if (valueSubmitted <= 8) return TXTWCL_Thank_youX_do_you_have_any_oth_ELL;
  return TXTWCL_ThatXs_great_to_hearX_can_you__ELL;
};

const NPS: React.FC = () => {
  const submissionUrl = useResolver(ResolverTag.NPSSubmission);
  const dismissalUrl = useResolver(ResolverTag.NPSDismissal);
  const followUpUrl = useResolver(ResolverTag.NPSFollowUp);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch<Dispatch>();
  const showThankYou = useSelector(select.nps.justSubmitted);
  const isDisplayableToUser = useSelector(select.nps.isDisplayableToUser);
  const loggedInBufferElapsed = useSelector(select.nps.userLoggedInBufferElapsed);
  const showQuestion = useSelector(select.nps.shouldDisplay);
  const phaseTwo = useFeature(FeatureFlag.NPSPhase2);
  const valueSubmitted = useSelector(select.nps.valueSubmitted);
  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);

  useEffect(() => {
    dispatch.nps.checkFirstAccess();
    dispatch.nps.checkPreviousInteraction();
  }, [dispatch.nps]);

  useEffect(() => {
    isClientAuthenticated && dispatch.nps.setFirstAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDisplayableToUser && !loggedInBufferElapsed) {
      let isMounted = true;
      const loggedInTimer = window.setTimeout(() => {
        isMounted && dispatch.nps.loggedInBufferElapsed();
      }, 180000);
      return () => {
        isMounted = false;
        window.clearTimeout(loggedInTimer);
      };
    }
  }, [isDisplayableToUser, loggedInBufferElapsed, dispatch.nps]);

  useEffect(() => {
    if (isDisplayableToUser && loggedInBufferElapsed && !showQuestion && !showThankYou) {
      let idleTimer: number;
      let isMounted = true;
      const resetTimer = () => {
        clearTimeout(idleTimer);
        idleTimer = window.setTimeout(() => {
          document.removeEventListener('mousemove', resetTimer);
          document.removeEventListener('keydown', resetTimer);
          isMounted && dispatch.nps.display();
        }, 10000);
      };
      document.addEventListener('mousemove', resetTimer);
      document.addEventListener('keydown', resetTimer);
      resetTimer();

      return () => {
        isMounted = false;
        window.clearTimeout(idleTimer);
        document.removeEventListener('mousemove', resetTimer);
        document.removeEventListener('keydown', resetTimer);
      };
    }
  }, [isDisplayableToUser, showThankYou, dispatch.nps, showQuestion, loggedInBufferElapsed]);

  if (showThankYou) {
    return (
      <div className={classNames(styles.npsContainer, styles.submitted)}>
        <StarsButton
          className={styles.closeButton}
          icon={<Icon icon={CloseMedium} size="lg" />}
          variant="text"
          onClick={() =>
            dispatch.nps.dismiss({
              dismissalUrl,
              dismissalReason: NpsDismissalReason.ThankYouDismissed
            })
          }
          title={formatMessage(TXTCLI_Remove)}
        />
        <span className={styles.thankYou}>
          <Translation {...TXTWCL_Thank_You} />
        </span>
        <Translation {...getSubmissionMessage(valueSubmitted, phaseTwo)} />
        {phaseTwo && followUpUrl && (
          <Button type="submit" onClick={() => dispatch.nps.accessFollowUp(followUpUrl)}>
            <Translation {...TXTCLI_Yes} />
          </Button>
        )}
      </div>
    );
  }

  if (!submissionUrl || !showQuestion || window.OneTrust?.IsAlertBoxClosed() === false) {
    return null;
  }

  return (
    <Expire timeout={30000} resetOnInteraction>
      <div className={styles.npsContainer}>
        <form>
          <fieldset role="radiogroup">
            <legend className={styles.text}>
              <Translation {...TXTWCL_On_a_scale_of_0___10X_how_like_ELL_1} />
            </legend>

            <div className={styles.selections}>
              {options.map((option) => (
                <div key={option} className={styles.scoreSelector}>
                  <input
                    type="radio"
                    id={`nps-${option}`}
                    name="npsValue"
                    value={option}
                    onChange={() => dispatch.nps.submit({ value: option, submissionUrl })}
                  />
                  <label htmlFor={`nps-${option}`}>{option}</label>
                </div>
              ))}
            </div>
          </fieldset>
        </form>
        <StarsButton
          className={styles.closeButton}
          icon={<Icon icon={CloseMedium} size="lg" />}
          variant="text"
          onClick={() =>
            dispatch.nps.dismiss({ dismissalUrl, dismissalReason: NpsDismissalReason.Dismissed })
          }
          title={formatMessage(TXTCLI_Remove)}
        />
      </div>
    </Expire>
  );
};

export default () => {
  const isNpsEnabled = useFeature(FeatureFlag.NPS);
  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);
  return isNpsEnabled && isClientAuthenticated ? <NPS /> : null;
};
